import get from 'lodash/get'

import VideoManager from '@/lib/video'

import { LOAD_QUIZZES } from '@/store/modules/quiz/types'
import { LOAD_THEMES } from '@/store/modules/theme/types'
import { LOAD_BOTTLES } from '@/store/modules/bottle/types'
import { LOAD_TASTINGS } from '@/store/modules/tasting/types'
import * as USER_TYPES from '@/store/modules/user/types'
import * as COMPLETION_TYPES from '@/store/modules/completion/types'
import { CHANGE_CONTENT_SYNC_STATUS, OFFLINE_INIT } from '@/store/modules/system/types'
import PodcastManager from '@/lib/podcast'

import parallel from 'async/parallel';


export default {
  /**
   * Will sync the datas between contentful/database and vuex
   */
  SYNC_DATA: async ({ dispatch }, { locale }) => {
    // Load themes, quizzes, bottles, tastings - All in parallel mode
    parallel([
      callback => dispatch(LOAD_THEMES, {locale, callback}),
      callback => dispatch(LOAD_TASTINGS, {locale, callback}),
      callback => dispatch(LOAD_QUIZZES, {locale, callback}),
      callback => dispatch(LOAD_BOTTLES, {locale, callback})
    ], (err, results) => {
      // Everything is ok ? Cool
      if (results.every(el => el === true)) {
        dispatch(CHANGE_CONTENT_SYNC_STATUS, { syncSuccess: true })
      } else {
        dispatch(CHANGE_CONTENT_SYNC_STATUS, { syncSuccess: false })
      }
      // Still do the offline init
      dispatch(OFFLINE_INIT)
    })
  },
  /**
   * Logout the user and reset the user related data
   */
  LOGOUT_USER: async ({ commit }) => {
    commit(COMPLETION_TYPES.RESET_STATE)
    commit(USER_TYPES.LOGOUT)
  },

  DOWNLOAD_OFFLINE_MASTERCLASS: async ({ state, getters }, { id, onProgress }) => {
    const vimeoIds = getVimeoIdsFromMasterclass(state, getters, id)
    const podcastUrls = getPodcastsUrlsFromMasterclass(state, getters, id)
    const progresses = {}
    const totalLength = vimeoIds.length + podcastUrls.length

    const jobVideo = vimeoIds.map(id =>
      VideoManager.fetchVideo(id, id, true, progress => {
        progresses[id] = progress

        if (typeof onProgress === 'function') {
          const totalProgress = Object.values(progresses).reduce(
            (acc, progress) => acc + progress,
            0
          )

          onProgress(totalProgress / totalLength)
        }
      })
    )
    const jobPodcast = podcastUrls.map(async url => {
      const anchorEp = await PodcastManager.getAnchorEpisode(url)
      if (anchorEp) {
        const anchorEpId = anchorEp.split('-').pop()

        const trackUrl = `https://podcast.vinoways.fr/https://anchor.fm/api/v3/episodes/${anchorEpId}`
        const blob = await PodcastManager.dispatchPodcastDownload(
          url,
          trackUrl,
          anchorEp,
          true,
          progress => {
            progresses[id] = progress

            if (typeof onProgress === 'function') {
              const totalProgress = Object.values(progresses).reduce(
                (acc, progress) => acc + progress,
                0
              )

              onProgress(totalProgress / totalLength)
            }
          }
        )
        PodcastManager.storePodcast(url, anchorEp, blob)
      }
    })

    return Promise.all([...jobVideo, ...jobPodcast])
  },
  DELETE_OFFLINE_MASTERCLASS: async ({ state, getters }, { id }) => {
    const vimeoIds = getVimeoIdsFromMasterclass(state, getters, id)
    const podcastUrls = getPodcastsUrlsFromMasterclass(state, getters, id)

    await VideoManager.bulkDeleteVideo(vimeoIds)
    await PodcastManager.bulkDeletePodcast(podcastUrls)
  }
}

/**
 * Extract the vimeo ids from the masterclass' lessons
 * @param {Object} state
 * @param {string} id
 * @returns {string[]}
 */
function getVimeoIdsFromMasterclass(state, getters, id) {
  const masterclass = getters.getThemeById(id)

  if (!masterclass) {
    throw new Error('Masterclass not found')
  }

  const lessons = masterclass.lessonSteps.map(lessonStep => lessonStep.lessons || []).flat()

  // https://player.vimeo.com/video/427628188
  return lessons.map(lesson => VideoManager.getVimeoIdFromLesson(lesson)).filter(Boolean)
}

/**
 * Extract the podcast urls from the masterclass' lessons
 * @param {Object} state
 * @param {string} id
 * @returns {string[]}
 */
function getPodcastsUrlsFromMasterclass(state, getters, id) {
  const masterclass = getters.getThemeById(id)

  if (!masterclass) {
    throw new Error('Masterclass not found')
  }

  const lessons = masterclass.lessonSteps.map(lessonStep => lessonStep.lessons || []).flat()

  // https://player.vimeo.com/video/427628188
  return lessons.map(lesson => lesson.podcastUrl).filter(Boolean)
}
